import { dateLocale } from "@/lib/dateTime";
import React from "react";
import { Link } from "react-router-dom";
import { Handshake } from "lucide-react";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { formatINR } from "@/lib/utils";
import { AgreementStatusBadge } from "@/components/agreements/AgreementStatusBadge";
import { AGREEMENT_TYPE_LABELS, type AgreementListRow } from "@/types/agreement";

const fmtDate = (d?: string | null) =>
  d ? new Date(d.length === 10 ? `${d}T00:00:00` : d).toLocaleDateString(dateLocale(), { day: "2-digit", month: "short", year: "numeric" }) : "—";

/** A customer's agreements: what is in force and until when, then the rest, newest first */
export function AgreementsWidget({ agreements }: { agreements: AgreementListRow[] }) {
  const today = new Date().toISOString().slice(0, 10);
  const inForce = agreements.filter((a) => a.agreement_status === "EXECUTED" && (a.end_date || "") >= today);
  return (
    <div className="bg-card border border-border/80 rounded-2xl shadow-xs overflow-hidden">
      <div className="flex items-center gap-3 px-5 py-4 border-b border-border/80">
        <div className="size-8 rounded-xl bg-primary/10 text-accent-foreground flex items-center justify-center shrink-0">
          <Handshake className="size-4" />
        </div>
        <div>
          <h3 className="text-sm font-semibold tracking-tight text-foreground">Agreements ({agreements.length})</h3>
          <p className="text-[11px] text-muted-foreground">
            Contracts with this customer{inForce.length ? ` · ${inForce.length} in force, ${formatINR(inForce.reduce((t, a) => t + Number(a.contract_value || 0), 0))}` : ""}. Make one from a deal whose quotation was accepted.
          </p>
        </div>
      </div>
      {agreements.length === 0 ? (
        <div className="p-8 text-center space-y-1">
          <div className="size-10 rounded-full bg-muted flex items-center justify-center mx-auto text-muted-foreground">
            <Handshake className="size-5" />
          </div>
          <p className="text-sm font-medium text-foreground">No agreements yet</p>
          <p className="text-xs text-muted-foreground">Agreements are made from a deal once the customer has accepted the quotation.</p>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Agreement</TableHead>
                <TableHead>Term</TableHead>
                <TableHead className="text-right">Value</TableHead>
                <TableHead>Status</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {agreements.map((a) => (
                <TableRow key={a.id}>
                  <TableCell>
                    <Link to={`/agreements/${a.id}`} className="font-mono text-xs font-semibold text-primary hover:underline">{a.display_number}</Link>
                    <div className="text-[11px] text-muted-foreground">{AGREEMENT_TYPE_LABELS[a.agreement_type] || a.agreement_type}{a.deal?.name ? ` · ${a.deal.name}` : ""}</div>
                  </TableCell>
                  <TableCell className="whitespace-nowrap text-sm">{fmtDate(a.effective_date)} → {fmtDate(a.end_date)}</TableCell>
                  <TableCell className="text-right font-semibold whitespace-nowrap">{formatINR(a.contract_value)}</TableCell>
                  <TableCell><AgreementStatusBadge status={a.agreement_status} /></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}
